import React from "react";
import { RequestStatusCards } from "../RequestStatusCards";

// Total Requests / Pending / Approved / Paid / Rejected / Cancelled summary
// cards above the Salary Advance table. Rendered through the shared
// RequestStatusCards so they match Team Workspace > Approvals > Advance
// Salary's AdvanceSalaryStatusCards. Cards double as status filters (see
// onCardClick); each status card's detail line is that bucket's total amount.
const CARD_KEYS = ["All", "Pending", "Approved", "Paid", "Rejected", "Cancelled"];
const SUMMARY_KEYS = ["Total Requests", "Pending", "Approved", "Paid", "Rejected", "Cancelled"];
const CARD_LABELS = { All: "Total Requests" };

// Amount can arrive as a plain number or a pre-formatted string; strip anything
// that isn't a digit or decimal point before summing so both shapes total
// correctly.
const parseAmount = (row) => {
  const raw = row?.amount ?? row?.effective_amount ?? 0;
  if (typeof raw === "number") return raw;
  const cleaned = String(raw).replace(/[^0-9.]/g, "");
  const value = parseFloat(cleaned);
  return Number.isNaN(value) ? 0 : value;
};

const formatAmount = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// The one card a request is counted under (or "" when its status is
// unrecognised). "Paid" tracks disbursement (disbursement_date set), which
// covers every post-disbursement status (Active/Partially-Paid/
// Partially-Recovered/Pre-Closed/Closed) since AdvanceService.disburse sets
// both fields together. Used for BOTH the card counts and the card-click
// filter, so the rows a card shows always equal the number on it.
export const advanceSummaryBucket = (row) => {
  const status = (row?.status || "").trim().toLowerCase();
  if (status === "cancelled" || status === "canceled") return "Cancelled";
  if (row?.disbursement_date) return "Paid";
  if (status === "pending") return "Pending";
  if (status === "approved") return "Approved";
  if (status === "rejected") return "Rejected";
  return "";
};

// Build { "Total Requests": {count, amount}, ... } from the full request list.
export const buildAdvanceSummary = (rows = []) => {
  const summary = SUMMARY_KEYS.reduce((acc, key) => {
    acc[key] = { count: 0, amount: 0 };
    return acc;
  }, {});

  rows.forEach((row) => {
    const amount = parseAmount(row);
    summary["Total Requests"].count += 1;
    summary["Total Requests"].amount += amount;

    const bucket = advanceSummaryBucket(row);
    if (bucket) {
      summary[bucket].count += 1;
      summary[bucket].amount += amount;
    }
  });

  return summary;
};

// onCardClick keeps its original contract: it receives "Total Requests" for
// the totals card and the status key otherwise.
export const AdvanceSummaryCards = ({
  summary,
  activeFilter,
  onCardClick,
  totalRequestsDetail = "This Year",
}) => {
  const counts = {};
  const details = { All: totalRequestsDetail };
  SUMMARY_KEYS.slice(1).forEach((key) => {
    counts[key] = summary?.[key]?.count || 0;
    details[key] = formatAmount(summary?.[key]?.amount);
  });

  return (
    <RequestStatusCards
      keys={CARD_KEYS}
      labels={CARD_LABELS}
      details={details}
      counts={counts}
      total={summary?.["Total Requests"]?.count || 0}
      activeStatus={activeFilter}
      onSelect={
        onCardClick ? (value) => onCardClick(value || "Total Requests") : undefined
      }
    />
  );
};
